import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import type { CSSProperties, RefObject } from 'react';
import { useMedia } from '../grid/hooks';

export type TConnatixOutstreamConfig = {
  embedScriptUrl: string;
  playerElementId: string;
};

const CONNATIX_OUTSTREAM_INJECTED_KEY = '__RIO_CONNATIX_OUTSTREAM_INJECTED';
const CONNATIX_OUTSTREAM_STATE_KEY = '__RIO_CONNATIX_OUTSTREAM_STATE';
const CONNATIX_WIDE_DESKTOP_BREAKPOINT_PX = 1750;
const CONNATIX_DEFAULT_WIDTH_PX = 300;
const CONNATIX_WIDE_DESKTOP_WIDTH_PX = 400;
const CONNATIX_OUTSTREAM_BASE_STYLE: CSSProperties = {
  position: 'fixed',
  right: 5,
  bottom: 5,
  aspectRatio: '16 / 9',
  zIndex: 1000,
  overflow: 'hidden',
  pointerEvents: 'none'
};
const CONNATIX_DOCKED_BASE_STYLE: CSSProperties = {
  width: '100%',
  height: '100%'
};
const CONNATIX_DOCKED_SLOT_SELECTOR = '[data-connatix-docked-slot="true"]';
const CONNATIX_DOCKED_VISIBLE_CLASS = 'rio-connatix-docked-visible';
const CONNATIX_DOCKED_STYLE_ELEMENT_ID = 'rio-connatix-docked-style';
const CONNATIX_DOCKED_STYLE_TEXT = `
${CONNATIX_DOCKED_SLOT_SELECTOR}.${CONNATIX_DOCKED_VISIBLE_CLASS} [id^="jwPlacementDiv_"],
${CONNATIX_DOCKED_SLOT_SELECTOR}.${CONNATIX_DOCKED_VISIBLE_CLASS} [id^="jwExperienceDiv_"] {
  width: 100% !important;
  height: 100% !important;
}

${CONNATIX_DOCKED_SLOT_SELECTOR}.${CONNATIX_DOCKED_VISIBLE_CLASS} cnx.cnx-main-container.cnx-always-floating {
  position: absolute !important;
  inset: 0 !important;
  width: 100% !important;
  height: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  transform: none !important;
  z-index: 1 !important;
}
`;

const useConnatixRegistrationEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

type TConnatixOutstreamProps = {
  config?: TConnatixOutstreamConfig;
};

type TConnatixRuntimeState = {
  container: HTMLDivElement;
  ownerToken: number;
};

type TConnatixWindow = Window &
  typeof globalThis & {
    __RIO_CONNATIX_OUTSTREAM_INJECTED?: boolean;
    __RIO_CONNATIX_OUTSTREAM_STATE?: TConnatixRuntimeState;
    RIO_OnProviderBlocked?: () => void;
    RIO_OnProviderLoaded?: () => void;
  };

type TConnatixPlacementContext = {
  dockedPlacementCount: number;
  registerDockedPlacement: () => { unregister: () => void };
};

const ConnatixPlacementContext = createContext<TConnatixPlacementContext>({
  dockedPlacementCount: 0,
  registerDockedPlacement: () => ({
    unregister: () => {
      /* nop */
    }
  })
});

export function buildConnatixPlacementScriptUrl(embedScriptUrl: string, playerElementId: string): string {
  const baseUrl = typeof window === 'undefined' ? 'https://raider.io/' : window.location.href;
  const url = new URL(embedScriptUrl, baseUrl);
  url.searchParams.set('divId', playerElementId);
  return url.toString();
}

type TConnatixPlayerTagProps = {
  config?: TConnatixOutstreamConfig;
  isDocked?: boolean;
  style?: CSSProperties;
};

let connatixOwnerToken = 0;

function ensureConnatixDockedStyleElement() {
  if (document.getElementById(CONNATIX_DOCKED_STYLE_ELEMENT_ID)) {
    return;
  }

  const styleElement = document.createElement('style');
  styleElement.id = CONNATIX_DOCKED_STYLE_ELEMENT_ID;
  styleElement.textContent = CONNATIX_DOCKED_STYLE_TEXT;
  document.head.appendChild(styleElement);
}

function isElementInViewport(element: HTMLElement) {
  const rect = element.getBoundingClientRect();

  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth
  );
}

function useConnatixDockedVisibility(containerRef: RefObject<HTMLDivElement | null>, enabled: boolean) {
  useConnatixRegistrationEffect(() => {
    if (!enabled) {
      return;
    }

    const win = window as Window & typeof globalThis & { IntersectionObserver?: typeof IntersectionObserver };
    const container = containerRef.current;
    const dockedSlot = container?.closest<HTMLElement>(CONNATIX_DOCKED_SLOT_SELECTOR);
    if (!dockedSlot) {
      return;
    }

    ensureConnatixDockedStyleElement();

    const setDockedVisibility = (isVisible: boolean) => {
      dockedSlot.classList.toggle(CONNATIX_DOCKED_VISIBLE_CLASS, isVisible);
    };
    const updateDockedVisibility = () => {
      setDockedVisibility(isElementInViewport(dockedSlot));
    };

    updateDockedVisibility();

    if (typeof win.IntersectionObserver === 'function') {
      const observer = new win.IntersectionObserver(([entry]) => {
        setDockedVisibility(Boolean(entry?.isIntersecting));
      });
      observer.observe(dockedSlot);

      return () => {
        observer.disconnect();
        dockedSlot.classList.remove(CONNATIX_DOCKED_VISIBLE_CLASS);
      };
    }

    win.addEventListener('scroll', updateDockedVisibility, { passive: true });
    win.addEventListener('resize', updateDockedVisibility);

    return () => {
      win.removeEventListener('scroll', updateDockedVisibility);
      win.removeEventListener('resize', updateDockedVisibility);
      dockedSlot.classList.remove(CONNATIX_DOCKED_VISIBLE_CLASS);
    };
  }, [containerRef, enabled]);
}

function clearConnatixContainer(container: HTMLDivElement) {
  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }
  container.style.pointerEvents = 'none';
}

function clearConnatixRuntimeState(win: TConnatixWindow, state: TConnatixRuntimeState) {
  clearConnatixContainer(state.container);
  delete win[CONNATIX_OUTSTREAM_INJECTED_KEY];
  delete win[CONNATIX_OUTSTREAM_STATE_KEY];
}

const ConnatixPlayerTag: FC<TConnatixPlayerTagProps> = ({ config, isDocked = false, style }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useConnatixDockedVisibility(containerRef, isDocked);

  useEffect(() => {
    const win = window as TConnatixWindow;
    const container = containerRef.current;
    if (!config?.embedScriptUrl || !config.playerElementId || !container) {
      console.error('[Connatix] missing placement embed configuration');
      return;
    }

    const ownerToken = ++connatixOwnerToken;
    const currentState = win[CONNATIX_OUTSTREAM_STATE_KEY];
    if (currentState?.container && currentState.container !== container) {
      clearConnatixRuntimeState(win, currentState);
    } else if (win[CONNATIX_OUTSTREAM_INJECTED_KEY]) {
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = buildConnatixPlacementScriptUrl(config.embedScriptUrl, config.playerElementId);
    win[CONNATIX_OUTSTREAM_INJECTED_KEY] = true;
    win[CONNATIX_OUTSTREAM_STATE_KEY] = {
      container,
      ownerToken
    };
    script.onload = () => {
      if (win[CONNATIX_OUTSTREAM_STATE_KEY]?.ownerToken !== ownerToken) {
        return;
      }

      container.style.pointerEvents = 'auto';
      win.RIO_OnProviderLoaded?.();
    };
    script.onerror = () => {
      if (win[CONNATIX_OUTSTREAM_STATE_KEY]?.ownerToken !== ownerToken) {
        return;
      }

      win.RIO_OnProviderBlocked?.();
    };

    container.appendChild(script);

    return () => {
      const state = win[CONNATIX_OUTSTREAM_STATE_KEY];
      if (state?.ownerToken === ownerToken) {
        clearConnatixRuntimeState(win, state);
      }
    };
  }, [config?.embedScriptUrl, config?.playerElementId]);

  return <div id={config?.playerElementId} ref={containerRef} style={style} />;
};

export const ConnatixPlacementProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [dockedPlacementCount, setDockedPlacementCount] = useState(0);
  const registerDockedPlacement = useCallback(() => {
    let isRegistered = true;
    setDockedPlacementCount((count) => count + 1);

    return {
      unregister: () => {
        if (!isRegistered) {
          return;
        }

        isRegistered = false;
        setDockedPlacementCount((count) => count - 1);
      }
    };
  }, []);
  const value = useMemo(
    () => ({
      dockedPlacementCount,
      registerDockedPlacement
    }),
    [dockedPlacementCount, registerDockedPlacement]
  );

  return <ConnatixPlacementContext.Provider value={value}>{children}</ConnatixPlacementContext.Provider>;
};

export function useConnatixDockedPlacementRegistration(enabled: boolean) {
  const { registerDockedPlacement } = useContext(ConnatixPlacementContext);

  useConnatixRegistrationEffect(() => {
    if (!enabled) {
      return;
    }

    const registration = registerDockedPlacement();

    return () => {
      registration.unregister();
    };
  }, [enabled, registerDockedPlacement]);
}

export const ConnatixDockedPlayer: FC<TConnatixOutstreamProps> = ({ config }) => (
  <ConnatixPlayerTag config={config} isDocked={true} style={CONNATIX_DOCKED_BASE_STYLE} />
);

export const ConnatixFloatingFallback: FC<TConnatixOutstreamProps> = ({ config }) => {
  const { dockedPlacementCount } = useContext(ConnatixPlacementContext);
  const [canMountFallback, setCanMountFallback] = useState(false);
  const isWideDesktop = useMedia([`(min-width: ${CONNATIX_WIDE_DESKTOP_BREAKPOINT_PX + 1}px)`], [true], false);
  const widthPx = isWideDesktop ? CONNATIX_WIDE_DESKTOP_WIDTH_PX : CONNATIX_DEFAULT_WIDTH_PX;
  const containerStyle = {
    ...CONNATIX_OUTSTREAM_BASE_STYLE,
    width: `min(${widthPx}px, calc(100vw - 10px))`
  };

  useEffect(() => {
    const mountTimeout = window.setTimeout(() => {
      setCanMountFallback(true);
    }, 0);

    return () => {
      window.clearTimeout(mountTimeout);
    };
  }, []);

  if (!canMountFallback || dockedPlacementCount > 0) {
    return null;
  }

  return <ConnatixPlayerTag config={config} style={containerStyle} />;
};

export const ConnatixOutstream = ConnatixFloatingFallback;
